<template>
  <FormulateForm
    ref="costConfigForm"
    name="costConfigForm"
  >
    <b-card-actions
      :title="$t('Dados da configuração de custo')"
      no-actions
      :show-loading="saving"
    >
      <b-row>
        <b-col md="5">
          <FormulateInput
            id="cost-config-name"
            v-model="formCostConfig.name"
            :label="$t('Nome')"
            name="name"
            type="text"
            class="required"
            validation="required"
            :instruction="$t('Identifica o contrato negociado com a adquirente')"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="cost-config-acquirer"
            v-model="formCostConfig.acquirer"
            :label="$t('Adquirente')"
            name="acquirer"
            type="vue-select"
            class="required"
            validation="required"
            :placeholder="$t('Selecione')"
            :options="acquirerOptions()"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="cost-config-effective-from"
            v-model="formCostConfig.effectiveFrom"
            :label="$t('Vigente de')"
            name="effectiveFrom"
            type="datepicker"
          />
        </b-col>
        <b-col md="2">
          <FormulateInput
            id="cost-config-effective-to"
            v-model="formCostConfig.effectiveTo"
            :label="$t('Vigente até')"
            name="effectiveTo"
            type="datepicker"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <p class="text-muted mb-0">
            <feather-icon icon="InfoIcon" />
            {{
              $t(
                'O custo financeiro de uma venda é o custo de operação mais o custo de antecipação de todas as parcelas. Por isso ele cresce com a quantidade de parcelas mesmo com o custo de operação fixo.'
              )
            }}
          </p>
        </b-col>
      </b-row>
    </b-card-actions>

    <!--
      Um painel por canal, gerados do mesmo laço de propósito: a tela existe para comparar os dois
      conjuntos de taxas, e blocos escritos à mão acabariam divergindo em rótulo ou precisão.
    -->
    <b-card-actions
      v-for="channel in channels"
      :key="channel.key"
      :title="channel.title"
      no-actions
      :show-loading="saving"
    >
      <b-row>
        <b-col>
          <p class="text-muted">
            <feather-icon icon="InfoIcon" />
            {{ channel.hint }}
          </p>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <FormulateInput
            :id="`cost-config-${channel.key}-operation-cash`"
            v-model="formCostConfig[channel.key].operationCostCashPercent"
            :label="$t('Custo de operação - à vista (%)')"
            :name="`${channel.key}OperationCostCashPercent`"
            type="text-number"
            :precision="4"
            suffix=" %"
            class="required"
            validation="required"
            :instruction="
              $t('Percentual cobrado sobre o total da venda paga em uma única parcela')
            "
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            :id="`cost-config-${channel.key}-operation-installment`"
            v-model="formCostConfig[channel.key].operationCostInstallmentPercent"
            :label="$t('Custo de operação - parcelado (%)')"
            :name="`${channel.key}OperationCostInstallmentPercent`"
            type="text-number"
            :precision="4"
            suffix=" %"
            class="required"
            validation="required"
            :instruction="
              $t('Percentual cobrado sobre o total da venda paga em duas ou mais parcelas')
            "
          />
        </b-col>
        <b-col md="4">
          <FormulateInput
            :id="`cost-config-${channel.key}-anticipation`"
            v-model="formCostConfig[channel.key].anticipationCostMonthlyPercent"
            :label="$t('Custo de antecipação ao mês (%)')"
            :name="`${channel.key}AnticipationCostMonthlyPercent`"
            type="text-number"
            :precision="4"
            suffix=" %"
            class="required"
            validation="required"
            :instruction="
              $t(
                'Percentual ao mês cobrado por parcela, proporcional ao prazo até o seu recebimento'
              )
            "
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <b-card-actions
      v-if="isEdition"
      :title="$t('Simulação de custo')"
      no-actions
      :show-loading="simulating"
    >
      <b-row class="mb-1">
        <b-col md="3">
          <FormulateInput
            id="simulation-sale-value"
            v-model="localSimulationValue"
            :label="$t('Valor de referência')"
            name="simulationSaleValue"
            type="text-number"
            currency="R$"
            :precision="2"
          />
        </b-col>
        <b-col md="3">
          <FormulateInput
            id="simulation-channel"
            v-model="localSimulationChannel"
            :label="$t('Canal')"
            name="simulationChannel"
            type="vue-select"
            :clearable="false"
            :options="channelOptions"
            :instruction="$t('As taxas variam entre presencial e online')"
          />
        </b-col>
        <b-col
          md="3"
          class="d-flex align-items-end pb-1"
        >
          <e-button
            :text="$t('Simular')"
            variant="primary"
            :busy="simulating"
            @click="runSimulation"
          />
        </b-col>
      </b-row>

      <b-table
        show-empty
        responsive
        striped
        small
        class="bordered"
        :empty-text="$t('Informe um valor e clique em Simular')"
        :fields="simulationFields"
        :items="simulationLines"
      >
        <template #cell(installments)="row">
          {{ row.item.installments }}x
          <!--
            A linha de 1 parcela usa o custo de operação À VISTA, não o de parcelado. Sem essa
            marcação, o salto de percentual entre 1x e 2x parece erro de cadastro em vez de duas
            taxas contratuais distintas.
          -->
          <b-badge
            v-if="row.item.usesCashOperationCost"
            variant="light-info"
            class="ml-50"
          >
            {{ $t('à vista') }}
          </b-badge>
        </template>
      </b-table>
    </b-card-actions>

    <e-crud-actions
      :busy="saving"
      @save="saveCostConfig"
      @cancel="cancel"
    />
  </FormulateForm>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { BRow, BCol, BTable, BBadge } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { cardDomain } from '@/mixins'
import EButton from '@/views/components/EButton.vue'
import ECrudActions from '@/views/components/ECrudActions.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BBadge,
    BCardActions,
    EButton,
    ECrudActions,
  },

  mixins: [cardDomain],

  data() {
    return {
      saving: false,
      simulating: false,
      localSimulationValue: 1000,
      localSimulationChannel: 'Physical',
    }
  },

  computed: {
    ...mapState(
      'pages/settings/financial/acquirerCostConfig/acquirerCostConfigMaintain',
      ['formCostConfig', 'simulationLines']
    ),

    isEdition() {
      return !!this.$route.params.id
    },

    /**
     * Canais de venda do contrato. As chaves batem com as propriedades do formulário e com o
     * enum do servidor — é o que permite um painel por canal sem um `if` por campo.
     */
    channels() {
      return [
        {
          key: 'physical',
          title: this.$t('Taxas do canal presencial'),
          hint: this.$t(
            'Aplicadas às vendas com o cartão lido no equipamento: PDV, ATM e SmartPOS.'
          ),
        },
        {
          key: 'online',
          title: this.$t('Taxas do canal online'),
          hint: this.$t(
            'Aplicadas às vendas sem o cartão presente, como App e site. A adquirente costuma cobrar mais caro neste canal por causa do risco de fraude e de chargeback.'
          ),
        },
      ]
    },

    channelOptions() {
      return [
        { value: 'Physical', label: this.$t('Presencial') },
        { value: 'Online', label: this.$t('Online') },
      ]
    },

    simulationFields() {
      return [
        {
          label: this.$t('Parcelas'),
          key: 'installments',
          class: 'text-center',
          thStyle: { width: '120px' },
        },
        {
          label: this.$t('Valor da parcela'),
          key: 'installmentValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Custo de operação'),
          key: 'operationCostValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Custo de antecipação'),
          key: 'anticipationCostTotal',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Custo financeiro'),
          key: 'financialCost',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Custo efetivo'),
          key: 'effectiveCostPercent',
          class: 'text-right',
          thStyle: { width: '130px' },
          formatter: value => `${Number(value).toFixed(2)}%`,
        },
        {
          label: this.$t('Líquido recebido'),
          key: 'netValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
      ]
    },
  },

  async mounted() {
    if (this.isEdition) {
      try {
        this.saving = true
        await this.stFetchById(this.$route.params.id)
        await this.runSimulation()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    }
  },

  methods: {
    ...mapActions(
      'pages/settings/financial/acquirerCostConfig/acquirerCostConfigMaintain',
      {
        stFetchById: 'fetchById',
        stSaveForm: 'saveForm',
        stFetchSimulation: 'fetchSimulation',
        stSetSimulationSaleValue: 'setSimulationSaleValue',
        stSetSimulationChannel: 'setSimulationChannel',
        stCleanState: 'cleanState',
      }
    ),

    async runSimulation() {
      try {
        this.simulating = true
        this.stSetSimulationSaleValue(this.localSimulationValue)
        this.stSetSimulationChannel(this.localSimulationChannel)
        await this.stFetchSimulation()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.simulating = false
      }
    },

    async saveCostConfig() {
      this.$refs.costConfigForm.showErrors()
      if (this.$refs.costConfigForm.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      try {
        this.saving = true
        await this.stSaveForm()
        this.showSuccess({ message: this.$t('Configuração de custo salva com sucesso') })
        this.$router.push({ name: 'acquirer-cost-config-list' })
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },

    cancel() {
      this.stCleanState()
      this.$router.push({ name: 'acquirer-cost-config-list' })
    },
  },
}
</script>

<style lang="scss" scoped></style>
